import { usePerformanceAccess } from "app/routes/dashboard/routes/takeoff-performance/access";
/* eslint-disable react/jsx-no-bind */
import {
  useTheme,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CalendarIcon,
  CloseIcon,
  CrewIcon,
  FleetIcon,
  OPSIcon,
  ReportIcon,
  SettingsIcon,
  MessagingIcon,
  SupportIcon,
  FormIcon,
} from "app/components/common/Icons";
import { ListItem } from "app/components/common/ListItem";
import SchoolRounded from "@material-ui/icons/SchoolRounded";
import { selBaseAirline, selAirlineBaseModules, selGrants } from "store/slices";
import { useSelector } from "react-redux";
import Avatar from "app/components/modules/Avatar";

const LMS_MODULE = "enr:lms:module";

const isModuleActive = (modules, moduleName) =>
  modules?.some((item) => {
    if (item.module !== moduleName || !item.enabled) return false;
    if (!item.expiration_date) return true;
    return new Date(item.expiration_date) >= new Date();
  });

const isGrantVisible = (grant) =>
  ["show", "view", "edit"].includes(grant?.action);

const isMenuVisible = (grant, menuKey) => {
  const menuAction = grant?.menu?.[menuKey]?.action;
  if (menuAction) return menuAction !== "deny";
  return isGrantVisible(grant);
};

const useStyles = makeStyles((theme) => ({
  more: {
    cursor: "pointer",
    color: `${theme.palette.black["80"]} !important`,

    marginLeft: "32px",
    "& .MuiButtonBase-root": {
      padding: 0,
    },
    "& .MuiListItemIcon-root": {
      transform: "none !important",
    },
    "& .MuiIconButton-root": {
      marginLeft: "0px",
    },
    "& .MuiSvgIcon-root": {
      color: `${theme.palette.black["80"]} !important`,
    },
  },
  moreItems: {
    cursor: "pointer",
    background: theme.palette.common.white,
    paddingBottom: "4px",
    marginLeft: "38px",
  },
  moreItem: {
    marginTop: "4px !important",
    paddingBottom: "0px",
    "& .MuiListItem-root ": {
      marginTop: 0,
    },
    "& .MuiListItemText-root": {
      marginTop: "0px",
      marginBottom: "0px",
    },

    "& .MuiTypography-root ": {
      color: `${theme.palette.primary.main} !important`,
    },
  },
  moreItemHover: {
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.main,
      borderLeftStyle: "solid",
      borderLeftWidth: "4px",
      borderRadius: "2px",
      borderLeftColor: theme.palette.primary.main,
    },
  },
  singleItem: {
    marginTop: "4px !important",
    paddingBottom: "0px",
    borderLeftStyle: "solid",
    borderLeftWidth: "4px",
    borderLeftColor: "white",
    paddingLeft: "28px",
    "& .MuiListItem-root ": {
      marginTop: 0,
    },
    "& .MuiListItemText-root": {
      marginTop: "0px",
      marginBottom: "0px",
    },

    "& .MuiTypography-root ": {
      color: `${theme.palette.black["80"]} !important`,
    },
  },
  singleItemHover: {
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.main,
      borderLeftStyle: "solid",
      borderLeftWidth: "4px",
      borderRadius: "2px",
      borderLeftColor: theme.palette.primary.main,
    },
  },
  singleItemActive: {
    color: theme.palette.primary.main,
    borderLeftStyle: "solid",
    borderLeftWidth: "4px",
    borderRadius: "2px",
    borderLeftColor: theme.palette.primary.main,
  },
  drawerPaper: {
    paddingTop: "24px",
    width: "264px",
  },
  drawerHeader: {
    marginLeft: "30px",
    display: "flex",
    "& .MuiAvatar-root": {
      width: "40px",
      height: "40px",
      marginRight: "8px",
    },
    "& .MuiTypography-root": {
      color: theme.palette.main["100"],
    },
    "& .MuiDivider-root": {
      paddingTop: "20px",
      paddingBottom: "20px",
    },
  },
}));

const Sidebar = ({ onClose, open }) => {
  const airline = useSelector(selBaseAirline);
  const grants = useSelector(selGrants);
  console.log("drawer-open", open);
  const classes = useStyles();
  const airlineBaseModules = useSelector(selAirlineBaseModules);
  const lmsGrant = grants?.module?.lms;
  const showLmsDashboard = isMenuVisible(lmsGrant, "dashboard");
  const showLmsCourse = isMenuVisible(lmsGrant, "course");
  const showLmsClass = isMenuVisible(lmsGrant, "class");

  const {
    canView: canViewPerformance,
    canViewSubmissions: canViewPerformanceSubmissions,
    canViewMenu: canViewPerformanceMenu,
  } = usePerformanceAccess();
  const sidebarItemsOne = [
    {
      name: "Performance",
      children: [
        ...(canViewPerformance
          ? [{ name: "Takeoff", link: "performance/takeoff" }]
          : []),
        ...(canViewPerformanceSubmissions
          ? [{ name: "Submissions", link: "performance/submissions" }]
          : []),
      ],
      icon: OPSIcon,
      hidden: !canViewPerformanceMenu,
    },
    {
      name: "OPS",
      children: [
        { name: "Timeline", link: "flights?view=timeline" },
        { name: "Table", link: "flights?view=table" },
        { name: "Calendar", link: "flights?view=calendar" },
      ],
      icon: OPSIcon,
      disabled: !airlineBaseModules.some(
        (item) => item.module === "enr:flight:module" && item.enabled
      ),
      hidden: grants.module.ops.action !== "view",
    },
    {
      name: "Crew",
      children: [{ name: "Crew Calendar", link: "crew-calendar" }],
      icon: CalendarIcon,
      link: "crew-calendar",
      disabled: !airlineBaseModules.some(
        (item) => item.module === "enr:flight:module" && item.enabled
      ),
      hidden: grants.module.crew.action !== "view",
    },
    {
      name: "Reports",
      link: "reports",
      icon: ReportIcon,
      disabled: !airlineBaseModules.some(
        (item) => item.module === "enr:report:module" && item.enabled
      ),
      hidden: grants.module.reports.action !== "view",
    },
    {
      name: "Forms",
      icon: FormIcon,
      link: "forms",
      disabled: !airlineBaseModules.some(
        (item) => item.module === "enr:form:module" && item.enabled
      ),
      hidden: grants.module.forms.action !== "view",
    },
    {
      name: "LMS",
      children: [
        {
          name: "Dashboard",
          link: "lms/dashboard",
          hidden: !showLmsDashboard,
        },
        {
          name: "Course",
          link: "lms/course",
          hidden: !showLmsCourse,
        },
        {
          name: "Class",
          link: "lms/class",
          hidden: !showLmsClass,
        },
      ],
      icon: SchoolRounded,
      disabled: !isModuleActive(airlineBaseModules, LMS_MODULE),
      hidden:
        !isGrantVisible(lmsGrant) ||
        (!showLmsDashboard && !showLmsCourse && !showLmsClass),
    },
    {
      name: "Messages",
      link: "messages",
      icon: MessagingIcon,
      disabled: !airlineBaseModules.some(
        (item) => item.module === "enr:message:module" && item.enabled
      ),
      hidden: grants.module.messaging.action !== "view",
    },
    {
      name: "Support",
      link: "ticketing",
      icon: SupportIcon,
      disabled: !airlineBaseModules.some(
        (item) => item.module === "enr:ticket:module" && item.enabled
      ),
      hidden: grants.module.ticketing.action !== "view",
    },
  ];
  const sidebarItemsTwo = [
    {
      name: "General settings",
      children: [
        { name: "General Settings", link: "general-settings" },
        { name: "Users", link: "users" },
        { name: "Admin Privileges", link: "admin-privileges", disabled: false },
        { name: "Email Templates", link: "email-templates", disabled: true },
      ],
      icon: SettingsIcon,
      link: "general-settings",
      hidden: grants.module.settings.action !== "view",
    },
    {
      name: "Fleet",
      children: [
        { name: "Fleet", link: "fleet" },
        { name: "Fleet Documents", link: "fleet-docs" },
        { name: "Seats", link: "seats" },
        { name: "Crew Position", link: "crew-position" },
      ],
      icon: FleetIcon,
      link: "fleet",
      disabled: !airlineBaseModules.some(
        (item) => item.module === "enr:fleet:module" && item.enabled
      ),
      hidden: grants.module.settings.action !== "view",
    },
    {
      name: "Crew",
      children: [
        { name: "Crew Currency", link: "crew-currency" },
        { name: "Crew Certificates", link: "crew-certificates" },
        { name: "Crew Ratings", link: "crew-ratings" },
        { name: "Ftl Settings", link: "ftl-settings" },
        { name: "Duties Setup", link: "duties-setup" },
      ],
      icon: CrewIcon,
      link: "crew-calendar",
      disabled: !airlineBaseModules.some(
        (item) => item.module === "enr:flight:module" && item.enabled
      ),
      hidden: grants.module.settings.action !== "view",
    },
    {
      name: "Flight",
      children: [
        { name: "Flight Editing", link: "flight-editing" },
        { name: "Company Flight", link: "flight-default" },
      ],
      icon: OPSIcon,
      link: "crew-calendar",
      disabled: !airlineBaseModules.some(
        (item) => item.module === "enr:flight:module" && item.enabled
      ),
      hidden: grants.module.settings.action !== "view",
    },
  ];
  // ===================== S E L E C T ====================

  return (
    <Drawer
      className={classes.drawer}
      anchor="left"
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <Grid className={classes.drawerHeader}>
        <Grid container alignItems="center">
          <Avatar imageId={airline.logo} className={classes.airlineAvatar} />
          <Typography variant="h6">{airline.name}</Typography>
        </Grid>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Grid>
      <Grid
        style={{
          marginRight: "12px",
          marginLeft: "12px",
          marginTop: "20px",
          marginBottom: "8px",
        }}
      >
        <Divider />
      </Grid>
      {sidebarItemsOne?.map((item, index) => {
        // `children` vs plain-link items are told apart by whether the item
        // *has* a children array at all, not by its current length: items
        // like "Performance" build `children` from grant checks and can
        // land on `[]` for a user who can't see any of its sub-pages — `[]`
        // is truthy, but `[].length` is falsy, so checking length used to
        // send an empty-children item down the plain-link branch below,
        // which crashes on `item.link.split(...)` since these items never
        // set `link` (they're `hidden` via `item.hidden`, but only once
        // rendering actually gets that far).
        if (item.children) {
          return (
            <Category
              item={item}
              onClose={onClose}
              hidden={item.hidden}
              key={`${item.name}-${index}`}
            />
          );
        } else {
          const path = window.location.pathname.split("/");
          const selectPath = path[path.length - 1];
          const itemPath = item.link?.split("/");
          const selectItemPath = itemPath[itemPath.length - 1];
          const selected = selectPath === selectItemPath;
          return (
            <Link
              key={item.name}
              to={`/app/dashboard/${item.link}`}
              onClick={(e) => {
                if (item.disabled) {
                  e.preventDefault();
                } else {
                  onClose();
                }
              }}
              aria-disabled={item.disabled}
              style={{
                textDecoration: "none",
                //pointerEvents: item.disabled ? "none" : "auto",
              }}
              hidden={item.hidden}
            >
              <ListItem
                key={item.name}
                className={`${classes.singleItem} ${
                  selected
                    ? classes.singleItemActive
                    : !item.disabled && classes.singleItemHover
                }`}
                startAdornment={<item.icon />}
                text={item.name}
                animation={false}
                disabled={item.disabled}
                //borderColor={!selected ? null : theme.palette.primary.main}
              />
            </Link>
          );
        }
      })}
      <Grid
        style={{
          marginRight: "12px",
          marginLeft: "12px",
          marginTop: "20px",
          marginBottom: "8px",
        }}
      >
        <Divider />
      </Grid>
      {sidebarItemsTwo?.map((item, index) => {
        return <Category item={item} onClose={onClose} hidden={item.hidden} />;
      })}
    </Drawer>
  );
};

export default Sidebar;

const SubCat = ({ item, selected, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  return (
    <Link
      key={item.name}
      to={`/app/dashboard/${item.link}`}
      style={{
        textDecoration: "none",
        pointerEvents: item.disabled ? "none" : "auto",
      }}
      onClick={(e) => {
        if (item.disabled) {
          e.preventDefault();
        } else {
          onClose();
        }
      }}
      disabled={item.disabled}
      aria-disabled={item.disabled}
    >
      <ListItem
        key={item.name}
        className={`${classes.moreItem} ${!selected && classes.moreItemHover}`}
        text={item.name}
        animation={false}
        borderColor={!selected ? null : theme.palette.primary.main}
        disabled={item.disabled}
      />
    </Link>
  );
};
const Category = ({ item, onClose, hidden }) => {
  const history = useHistory();
  const [openMore, setOpenMore] = useState(false);
  const classes = useStyles();
  const children = item.children?.filter((child) => !child.hidden);
  const toggleCollapse = (e) => {
    setOpenMore(!item.disabled && !openMore);
  };
  return (
    <ListItem
      hidden={hidden}
      onClick={toggleCollapse}
      key={item.name}
      text={item.name}
      disabled={item.disabled}
      aria-disabled={item.disabled}
      endAdornment={
        <Grid>
          {openMore ? (
            <IconButton disabled={item.disabled}>
              <ArrowUpIcon
                className={classes.arrowUp}
                onClick={toggleCollapse}
              />
            </IconButton>
          ) : (
            <IconButton disabled={item.disabled}>
              <ArrowDownIcon
                className={classes.arrowDown}
                onClick={toggleCollapse}
              />
            </IconButton>
          )}
        </Grid>
      }
      startAdornment={<item.icon />}
      collapse={openMore}
      className={classes.more}
      collapsibleItems={
        <Grid className={classes.moreItems}>
          {children?.map((item) => {
            const path = history.location.pathname.split("/");
            const selectPath = path[path.length - 1];
            const itemPath = item.link?.split("/");
            const selectItemPath = itemPath[itemPath.length - 1];
            const selected = selectPath === selectItemPath;

            return <SubCat item={item} selected={selected} onClose={onClose} />;
          })}
        </Grid>
      }
    />
  );
};
